import React from 'react';
import PropTypes from 'prop-types';
import { brandColors, textColors } from '../../tokens';

/**
 * Radio Component
 * 
 * Standardized radio button component for forms.
 * Use with RadioGroup for managing multiple radio options.
 * 
 * @example
 * // Basic radio
 * <Radio
 *   checked={plan === 'pro'}
 *   onChange={() => setPlan('pro')}
 *   label="Pro Plan"
 * />
 * 
 * @example
 * // Radio group
 * <RadioGroup
 *   name="plan"
 *   value={selectedPlan}
 *   onChange={setSelectedPlan}
 *   options={[
 *     { value: 'free', label: 'Free' },
 *     { value: 'pro', label: 'Pro' },
 *     { value: 'enterprise', label: 'Enterprise' },
 *   ]}
 * />
 */
const Radio = ({
  // Value and change handlers
  checked = false,
  onChange,
  onBlur,
  onFocus,
  
  // Radio attributes
  name,
  id,
  value,
  disabled = false,
  required = false,
  
  // Label
  label = '',
  labelPosition = 'right', // 'left' | 'right'
  
  // Sizing
  size = 'md', // 'sm' | 'md' | 'lg'
  
  // States
  error = '',
  
  // Styling
  className = '',
  labelClassName = '',
  
  // Accessibility
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  
  // Additional props
  ...rest
}) => {
  // Determine state
  const hasError = Boolean(error);
  
  // Auto-generate ID for aria-describedby
  const errorId = id ? `${id}-error` : undefined;
  
  // Build aria-describedby
  let describedBy = ariaDescribedBy || '';
  if (hasError && errorId) describedBy = describedBy ? `${describedBy} ${errorId}` : errorId;
  
  // Size classes for radio
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };
  
  // Label text size classes
  const labelSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };
  
  // Base radio classes
  const baseClasses =
    'rounded-full border transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-dark-900';
  
  // State classes
  let stateClasses = '';
  if (disabled) {
    stateClasses = 'border-gray-300 dark:border-dark-700 bg-gray-100 dark:bg-dark-800 cursor-not-allowed';
  } else if (hasError) {
    stateClasses = 'border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 focus:ring-red-500 dark:focus:ring-red-400';
  } else {
    stateClasses = `border-gray-300 dark:border-dark-600 ${brandColors.primary} ${brandColors.focus} ${brandColors.borderHover} cursor-pointer`;
  }
  
  // Combine radio classes
  const radioClasses = [
    baseClasses,
    sizeClasses[size],
    stateClasses,
    className,
  ].filter(Boolean).join(' ');
  
  // Combine label classes
  const combinedLabelClasses = [
    'select-none',
    labelSizeClasses[size],
    disabled ? 'text-gray-400 dark:text-dark-500 cursor-not-allowed' : `${textColors.secondary} cursor-pointer`,
    labelClassName,
  ].filter(Boolean).join(' ');
  
  // Radio element
  const radioElement = (
    <input
      type="radio"
      checked={checked}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      name={name}
      id={id}
      value={value}
      disabled={disabled}
      required={required}
      className={radioClasses}
      aria-label={ariaLabel || (label ? undefined : 'Radio')}
      aria-describedby={describedBy || undefined}
      aria-invalid={ariaInvalid !== undefined ? ariaInvalid : hasError}
      {...rest}
    />
  );
  
  // Label element
  const labelElement = label ? (
    <span className={combinedLabelClasses}>
      {label}
    </span>
  ) : null;
  
  return (
    <div>
      {/* Radio with label */}
      <label className="inline-flex items-center">
        {labelPosition === 'left' && labelElement && (
          <>
            {labelElement}
            <div className="ml-3">
              {radioElement}
            </div>
          </>
        )}
        
        {labelPosition === 'right' && (
          <>
            {radioElement}
            {labelElement && (
              <div className="ml-2">
                {labelElement}
              </div>
            )}
          </>
        )}
      </label>
      
      {/* Error Message */}
      {hasError && (
        <p id={errorId} className="mt-1 text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
};

Radio.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  
  name: PropTypes.string,
  id: PropTypes.string,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  
  label: PropTypes.string,
  labelPosition: PropTypes.oneOf(['left', 'right']),
  
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  
  error: PropTypes.string,
  
  className: PropTypes.string,
  labelClassName: PropTypes.string,
  
  'aria-label': PropTypes.string,
  'aria-describedby': PropTypes.string,
  'aria-invalid': PropTypes.bool,
};

/**
 * RadioGroup Component
 * 
 * Wrapper component for managing a group of radio buttons.
 * Simplifies radio group creation with automatic name handling.
 */
export const RadioGroup = ({
  name,
  value,
  onChange,
  options = [],
  size = 'md',
  disabled = false,
  error = '',
  orientation = 'vertical', // 'vertical' | 'horizontal'
  className = '',
  'aria-label': ariaLabel,
}) => {
  const groupId = React.useId();
  const errorId = error ? `${groupId}-error` : undefined;
  
  const containerClasses = [
    orientation === 'vertical' ? 'space-y-2' : 'flex flex-wrap gap-4',
    className,
  ].filter(Boolean).join(' ');
  
  return (
    <div 
      role="radiogroup" 
      aria-label={ariaLabel}
      aria-describedby={errorId}
    >
      <div className={containerClasses}>
        {options.map((option, index) => (
          <Radio
            key={option.value || index}
            name={name}
            id={option.id || `${groupId}-${option.value}`}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
            label={option.label}
            disabled={disabled || option.disabled}
            size={size}
            aria-describedby={errorId}
          />
        ))}
      </div>
      
      {/* Error Message */}
      {error && (
        <p id={errorId} className="mt-2 text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
};

RadioGroup.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
      id: PropTypes.string,
    })
  ),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  disabled: PropTypes.bool,
  error: PropTypes.string,
  orientation: PropTypes.oneOf(['vertical', 'horizontal']),
  className: PropTypes.string,
  'aria-label': PropTypes.string,
};

export default Radio;
